"use client";

import classNames from "classnames";
import { useCarousel } from "component/use-carousel-hook";
import {
  Children, ReactNode, useEffect, useState,
} from "react";
import HideIf from "util/HideIf";
import SliderArrow from "./SliderArrow";
import styles from "./slider-v2.module.scss";

interface Props {
  currentImageIndex?: number;
  slidesInView?: number;
  ulClassName?: string;
  onArrowClick?: (index: number) => void;
  onDotClick?: (index: number) => void;
  children: ReactNode[];
  autoplay?: boolean;
  autoplaySpeed?: number;
  dotsClassName?: string;
  activeDotClassName?: string;
  showArrows?: boolean;
  lazyLoad?: boolean;
  className?: string;
  showDots?: boolean;
  showArrowOnHover?: boolean;
  partialSlideInView?: boolean;
  handleImageClick?: () => void;
  onImageChange?: (index: number) => void;
}

function SliderV2(props: Props) {
  const {
    currentImageIndex, slidesInView = 1, ulClassName, onArrowClick, onDotClick, children,
    autoplay, autoplaySpeed, dotsClassName = "", activeDotClassName = "", showArrows = true, lazyLoad = false,
    className = "", showDots = true, showArrowOnHover = false, handleImageClick, partialSlideInView, onImageChange = null,
  } = props;
  const {
    ref: ulRef, previous, next, current, setCurrent,
  } = useCarousel<HTMLUListElement>();
  const [touchStartX, setTouchStartX] = useState();
  const [loadImages, setLoadImages] = useState(false);

  // when currentImageIndex is updated, slider is also updated to keep product & thumbnail slider in sync
  useEffect(() => {
    if (typeof currentImageIndex === "number" && currentImageIndex >= 0 && currentImageIndex < children.length) {
      setCurrent(currentImageIndex);
    }
  }, [currentImageIndex]);

  function autoplayFunc() {
    if (lazyLoad) {
      setLoadImages(true);
    }
    if (current + 1 < children.length) {
      setCurrent(current + 1);
    } else {
      setCurrent(0);
    }
  }

  useEffect(() => {
    if (onImageChange) {
      onImageChange(current);
    }
  },
  [current]);

  useEffect(() => {
    let interval;
    if (autoplay) {
      interval = setInterval(autoplayFunc, autoplaySpeed);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [current]);

  // Number of slides to show in viewport
  const slideWidth = (100 / Math.min(slidesInView, children.length)) - (partialSlideInView ? 1.5 : 0);

  // if onArrowClick function is present, there's no need to call previous(), next() bcz correct image will load bcz of above useEffect
  function handleOnPrevClick() {
    if (onArrowClick) {
      onArrowClick(current === 0 ? children.length - 1 : current - 1);
    } else {
      previous();
    }
  }

  function handleOnNextClick() {
    if (onArrowClick) {
      onArrowClick(current === children.length - 1 ? 0 : current + 1);
    } else {
      next();
    }
  }

  function handleDotClick(index: number) {
    setCurrent(index);
    if (onDotClick) {
      onDotClick(index);
    }
  }

  function handleTouchStart(event) {
    if (lazyLoad) {
      setLoadImages(true);
    }
    setTouchStartX(event.touches[0].clientX);
  }

  function handleTouchEnd(event) {
    handleGesture(event.changedTouches[0].clientX);
  }

  function handleGesture(touchEndX) {
    if (touchEndX < touchStartX) {
      handleOnNextClick();
    } else if (touchEndX > touchStartX) {
      handleOnPrevClick();
    }
  }

  function handleMouseEnter() {
    if (lazyLoad) {
      setLoadImages(true);
    }
  }

  return (
    <div
      className={`${styles.slider_container} ${className}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseEnter}
    >

      <HideIf show={showArrows && Children.count(children) > slidesInView}>
        <SliderArrow arrowType="prev" onClick={handleOnPrevClick} showArrowOnHover={showArrowOnHover} />
        <SliderArrow arrowType="next" onClick={handleOnNextClick} showArrowOnHover={showArrowOnHover} />
      </HideIf>
      <ul
        ref={ulRef}
        className={`${styles.ul} ${ulClassName}`}
      >
        {children.map((child: any, index: any) => (
          <li
            key={index}
            className={styles.li}
            style={{ width: `${slideWidth}%` }}
            onClick={() => {
              if (handleImageClick) {
                handleImageClick();
              }
            }}
          >
            {/* Load only images which are in viewport when page loads */}
            {lazyLoad && index >= (100 / slideWidth) && !loadImages ? null : child}
          </li>
        ))}
      </ul>
      <ul className={classNames(styles.slider_dots, dotsClassName, {
        [styles.hide_dots]: !showDots,
      })}
      >
        {children.map((_, index) => (
          <li key={index} className={styles.slider_dots_li}>
            <button
              type="button"
              className={classNames("btn", styles.slider_dots_btn, {
                [styles.active_dot]: current === index,
                [activeDotClassName]: current === index,
              })}
              onClick={() => handleDotClick(index)}
            >
              {index}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SliderV2;
